import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { FiAlertCircle, FiArrowRight, FiBookOpen, FiCalendar, FiCheckCircle, FiClock, FiCreditCard, FiDollarSign, FiGrid, FiHome, FiLayers, FiPlus, FiRefreshCw, FiTrendingUp, FiUserCheck, FiUserPlus, FiUsers } from 'react-icons/fi';
import { FaRupeeSign } from 'react-icons/fa';
import UiSelect from '../components/UiSelect.jsx';
import API from '../api/index.js';
import { getDashboardData, getStudentsSummary, getStudentPaymentPlans, getStudentOutstanding, createPayment, deleteStudent } from '../services/api.js';
import '../styles/pages/dashboard.css';
import axios from '../api/index.js';


const safeList = (res) => {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  if (Array.isArray(res.data)) return res.data;
  if (Array.isArray(res.data?.data)) return res.data.data;
  if (Array.isArray(res.data?.results)) return res.data.results;
  for (const key of ['students', 'libraries', 'rooms', 'seats', 'payments', 'payment_plans', 'plans', 'data', 'results']) {
    if (Array.isArray(res.data?.[key])) return res.data[key];
    if (Array.isArray(res[key])) return res[key];
  }
  return [];
};

const studentDue = (student = {}) => Number(student.amount_due ?? student.outstanding_amount ?? student.total_due ?? student.balance_due ?? student.due_amount ?? student.due ?? student.outstanding ?? 0) || 0;
const subscriptionEnd = (student = {}) => student.subscription_end || student.subscriptionend || student.subscriptionEnd || student.subscription_end_date || student.end_date || student.plan_end_date || '';
const daysUntil = (dateValue) => {
  if (!dateValue) return null;
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return null;
  const today = new Date(); today.setHours(0,0,0,0); date.setHours(0,0,0,0);
  return Math.ceil((date.getTime() - today.getTime()) / 86400000);
};
const statValue = (dashboard, keys, fallback = 0) => {
  const stats = dashboard?.stats || {};
  for (const key of keys) {
    const value = stats[key] ?? dashboard?.[key];
    if (value !== undefined && value !== null && value !== '') return Number(value);
  }
  return fallback;
};
const RupeeAmount = ({ value }) => <span className="rupee-amount"><FaRupeeSign aria-hidden="true" />{Number(value || 0).toLocaleString('en-IN')}</span>;
const studentKey = (student = {}) => String(student.studentid ?? student.student_id ?? student.id ?? '');
const paymentAmount = (payment = {}) => Number(payment.amount ?? payment.received_amount ?? payment.paid_amount ?? 0) || 0;
const paymentDate = (payment = {}) => payment.created_at || payment.payment_date || payment.date || payment.paid_at || '';
function usePaged(items, pageSize = 5) {
  const [page, setPage] = useState(1);
  const pages = Math.max(1, Math.ceil(items.length / pageSize));
  useEffect(() => { if (page > pages) setPage(pages); }, [page, pages]);
  const start = (page - 1) * pageSize;
  return { page, pages, rows: items.slice(start, start + pageSize), setPage };
}

function DashPager({ page, pages, setPage }) {
  if (pages <= 1) return null;
  return (
    <div className="dash-pagination">
      <span>Page {page} of {pages}</span>
      <div className="dash-pagination-actions">
        <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</button>
        <button type="button" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</button>
      </div>
    </div>
  );
}

function DashboardDataSkeleton() {
  return (
    <div className="dash-skeleton" role="status" aria-label="Loading dashboard data">
      <div className="dash-skel-kpis">
        {Array.from({ length: 8 }, (_, index) => (
          <div className="dash-skel-kpi" key={index}>
            <span className="dash-skel-icon" />
            <div className="dash-skel-stack">
              <span className="dash-skel-line w-60" />
              <span className="dash-skel-line value" />
              <span className="dash-skel-line w-80" />
            </div>
          </div>
        ))}
      </div>
      <section className="dash-skel-card">
        <div className="dash-skel-head"><span className="dash-skel-line w-180" /><span className="dash-skel-line w-100" /></div>
        <div className="dash-skel-revenue-grid">
          {Array.from({ length: 4 }, (_, index) => (
            <div className="dash-skel-revenue-card" key={index}>
              <span className="dash-skel-icon small" />
              <span className="dash-skel-line w-80" />
              <span className="dash-skel-line value" />
            </div>
          ))}
        </div>
      </section>
      <div className="dash-skel-two-column">
        {Array.from({ length: 2 }, (_, index) => (
          <section className="dash-skel-card" key={index}>
            <div className="dash-skel-head"><span className="dash-skel-line w-180" /><span className="dash-skel-line w-80" /></div>
            <div className="dash-skel-table">
              {Array.from({ length: 5 }, (__, row) => (
                <div className="dash-skel-table-row" key={row}>
                  <span className="dash-skel-line w-90" />
                  <span className="dash-skel-line w-60" />
                  <span className="dash-skel-line w-40" />
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
      <div className="dash-skel-two-column">
        {Array.from({ length: 2 }, (_, index) => (
          <section className="dash-skel-card" key={index}>
            <div className="dash-skel-head"><span className="dash-skel-line w-180" /></div>
            <span className="dash-skel-chart" />
          </section>
        ))}
      </div>
      <div className="dash-skel-lower-grid">
        {Array.from({ length: 3 }, (_, index) => (
          <section className="dash-skel-card" key={index}>
            <div className="dash-skel-head"><span className="dash-skel-line w-140" /></div>
            <div className="dash-skel-list">
              {Array.from({ length: 4 }, (__, row) => (
                <div className="dash-skel-list-row" key={row}>
                  <span className="dash-skel-icon small" />
                  <span className="dash-skel-line w-120" />
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
      <div className="dash-skel-two-column">
        {Array.from({ length: 2 }, (_, index) => (
          <section className="dash-skel-card" key={index}>
            <div className="dash-skel-head"><span className="dash-skel-line w-180" /></div>
            <div className="dash-skel-table">
              {Array.from({ length: 4 }, (__, row) => (
                <div className="dash-skel-table-row" key={row}>
                  <span className="dash-skel-line w-90" />
                  <span className="dash-skel-line w-60" />
                  <span className="dash-skel-line w-40" />
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

export function Dashboard({ setPage, openStudent, toast, selectedLibrary = 'All Libraries', libraries = [] }) {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [students, setStudents] = useState([]);
  const [payments, setPayments] = useState([]);
  const [canViewFinancials, setCanViewFinancials] = useState(false);
  const [deletingStudentId, setDeletingStudentId] = useState(null);
  const [savingPayment, setSavingPayment] = useState(false);

  const getTimeBasedGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return 'Good morning';
    if (hour >= 12 && hour < 17) return 'Good afternoon';
    if (hour >= 17 && hour < 21) return 'Good evening';
    return 'Good night';
  };

  const getHeaderSubtitle = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 17) return 'Track students, seat occupancy, and collections across your libraries.';
    if (hour >= 17 && hour < 21) return "Review today's collections and follow up on pending dues.";
    return 'Everything is up to date and ready for review.';
  };

  const toTitleCase = (value = '') => String(value).trim().replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1));

  // === PAYMENT MODAL STATE FOR RENEW / PAY REMAINING ===
  const [showAddModal, setShowAddModal] = useState(false);
  const [studentPlans, setStudentPlans] = useState([]);
  const [outstandingInfo, setOutstandingInfo] = useState(null);
  const [seatUpgrade, setSeatUpgrade] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedPlanAmount, setSelectedPlanAmount] = useState(null);
  const [formData, setFormData] = useState({
    studentid:'', planid:'', amount:'', receivedAmount:'',
    paymentmethod:'cash', transaction_id:'', status:'completed',
    sendReminder:false, renewalRequested:false,
    paymentType:'subscription', upgradeAmount:'', payBoth:false
  });
  const [errors, setErrors] = useState({});

  const activeLibrary = useMemo(() => {
    if (!selectedLibrary || selectedLibrary === 'All Libraries' || selectedLibrary === 'all') return null;
    return libraries.find(library =>
      String(library.id ?? library.library_id) === String(selectedLibrary) ||
      String(library.library_name || '').toLowerCase() === String(selectedLibrary).toLowerCase()
    ) || null;
  }, [libraries, selectedLibrary]);
  const activeLibraryId = activeLibrary?.id ?? activeLibrary?.library_id ?? (typeof selectedLibrary === 'number' ? selectedLibrary : null);
  const allLibraries = !selectedLibrary || selectedLibrary === 'All Libraries' || selectedLibrary === 'all';

  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem('userinfo') || '{}');
      const role = String(user.role || '').toLowerCase().replace(/[\s_]/g, '');
      setCanViewFinancials(role === 'tenantowner');
    } catch {
      setCanViewFinancials(false);
    }
  }, []);

  const loadDashboard = useCallback(async (forceRefresh = false, background = false) => {
    if (background) setRefreshing(true);
    else setLoading(true);
    const libraryId = activeLibraryId || null;
    const scoped = (url) => API.get(url, libraryId ? { params: { library_id: libraryId } } : undefined).catch(() => []);

    // The consolidated endpoint already contains stats, fees, expiring
    // students, plan mix and seat breakdown. Payments and an initial student
    // summary are the only extra data the dashboard needs.
    const dashboardRequest = getDashboardData(libraryId, forceRefresh).catch(error => {
      console.error('Dashboard summary failed', error);
      return null;
    });
    const paymentsRequest = scoped('/api/payments');
    const studentsRequest = background ? Promise.resolve(null) : getStudentsSummary(libraryId, forceRefresh).catch(() => null);

    try {
      const completeData = await dashboardRequest;
      if (completeData) {
        setDashboard(completeData?.dashboard?.dashboard || completeData?.dashboard || completeData || {});
      }
      if (!background) setLoading(false);

      const [paymentsRes, studentsRes] = await Promise.all([paymentsRequest, studentsRequest]);
      setPayments(safeList(paymentsRes));
      if (studentsRes) setStudents(safeList(studentsRes));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [activeLibraryId]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  // Listen for data update events from other components
  useEffect(() => {
    const handlePaymentUpdate = () => {
      console.log('🔄 Dashboard: Payment data updated, refreshing...');
      loadDashboard(true, false); // Don't use background mode for payment updates
    };

    const handleStudentUpdate = () => {
      console.log('🔄 Dashboard: Student data updated, refreshing...');
      loadDashboard(true, false); // Don't use background mode for student updates
    };

    window.addEventListener('paymentDataUpdated', handlePaymentUpdate);
    window.addEventListener('studentDataUpdated', handleStudentUpdate);

    return () => {
      window.removeEventListener('paymentDataUpdated', handlePaymentUpdate);
      window.removeEventListener('studentDataUpdated', handleStudentUpdate);
    };
  }, [loadDashboard]);

  const seatStats = useMemo(() => {
    const total = statValue(dashboard, ['total_seats', 'totalseats']);
    const occupied = statValue(dashboard, ['occupied_seats', 'occupiedseats']);
    const available = statValue(dashboard, ['available_seats', 'availableseats']);
    const reserved = statValue(dashboard, ['reserved_seats', 'reservedseats'], Math.max(0, total - occupied - available));
    const used = occupied + reserved;
    return { total, occupied, available, reserved, used, percent: total ? Math.round((used / total) * 100) : 0 };
  }, [dashboard]);

 const revenue = useMemo(() => {
     // from complete API first
     const raw = dashboard?.revenue_by_month || dashboard?.monthly_revenue || [];
     if(raw.length){
       const months = raw.map(r=> r.month || r.label).slice(-7);
       const monthly = raw.map(r=> Number(r.total||r.amount||0)).slice(-7);
       return { months, monthly, max: Math.max(...monthly, 100000) };
     }
     // fallback from payments parallel
     if(payments.length){
       const map={};
       payments.forEach(p=>{
         const m=new Date(p.created_at||p.payment_date).toLocaleString('en',{month:'short'});
         map[m]=(map[m]||0)+Number(p.amount||0);
       });
       const months = Object.keys(map).slice(-7);
       const monthly = Object.values(map).slice(-7);
       if(months.length) return { months, monthly, max: Math.max(...monthly, 100000) };
     }
     const monthRevenue = Number(dashboard?.stats?.monthlyrevenue ?? dashboard?.monthlyrevenue ?? dashboard?.monthly_revenue ?? 0);
     if(monthRevenue){
       const label = new Date().toLocaleString('en',{month:'short'});
       return { months:[label], monthly:[monthRevenue], max: Math.max(monthRevenue, 100000) };
     }
     return { months:['Apr','May','Jun','Jul','Aug','Sep','Oct'], monthly:[0,0,0,0,0,0,0], max:100000 };
   }, [dashboard, payments]);
 
   const growth = useMemo(() => {
     const raw = dashboard?.student_growth || dashboard?.students_by_month || [];
     if(raw.length){
       const months = raw.map(r=> r.month || r.label).slice(-7);
       const counts = raw.map(r=> Number(r.count||r.total||0)).slice(-7);
       return { months, counts, max: Math.max(...counts, 100) };
     }
     if(students.length){
       const byMonth={};
       students.forEach(s=>{
         const m=new Date(s.created_at||s.join_date).toLocaleString('en',{month:'short'});
         if(m!=='Invalid Date') byMonth[m]=(byMonth[m]||0)+1;
       });
       const months=Object.keys(byMonth).slice(-7);
       const counts=Object.values(byMonth).slice(-7);
       if(months.length) return { months, counts, max: Math.max(...counts, 100) };
     }
     return { months:['Apr','May','Jun','Jul','Aug','Sep','Oct'], counts:[0,0,0,0,0,0,0], max:100 };
   }, [dashboard, students]);
 

  const expiringSoon = useMemo(() => {
    const serverRows = [dashboard?.expiringsoon, dashboard?.expiring_soon, dashboard?.students_expiring_soon].find(rows => Array.isArray(rows)) || [];
    const normalize = rows => rows
      .map(student => ({ ...student, _daysRemaining: daysUntil(subscriptionEnd(student)) }))
      .filter(student => student._daysRemaining !== null && student._daysRemaining <= 7);
    const merged = new Map();
    [...normalize(students), ...normalize(serverRows)].forEach(student => {
      const key = studentKey(student) || `${student.name || student.full_name || ''}-${subscriptionEnd(student)}`;
      if (!merged.has(key)) merged.set(key, student);
    });
    return [...merged.values()].sort((a, b) => {
      const daysA = a._daysRemaining;
      const daysB = b._daysRemaining;
      if (daysA < 0 && daysB >= 0) return -1;
      if (daysA >= 0 && daysB < 0) return 1;
      if (daysA < 0 && daysB < 0) return daysB - daysA;
      return daysA - daysB;
    });
  }, [dashboard, students]);

  const dueRows = useMemo(() => {
    const serverRows = [dashboard?.students_with_due, dashboard?.students_with_outstanding, dashboard?.outstanding_students].find(rows => Array.isArray(rows) && rows.length) || [];
    const rows = serverRows.length ? serverRows : students.filter(student => studentDue(student) > 0 || Number(student.seat_upgrade_due || 0) > 0);
    return rows
      .map(student => ({ ...student, _amountDue: Math.max(0, studentDue(student)) || Math.max(0, Number(student.seat_upgrade_due || 0)) }))
      .filter(student => student._amountDue > 0);
  }, [dashboard, students]);

  const outstandingStudents = useMemo(() => [...dueRows].sort((a, b) => b._amountDue - a._amountDue), [dueRows]);
  const outstandingTotal = useMemo(() => dueRows.reduce((sum, student) => sum + student._amountDue, 0), [dueRows]);

  const recentStudentsLive = useMemo(() => {
    const serverRows = [dashboard?.recentstudents, dashboard?.recent_students, dashboard?.recentStudents].find(rows => Array.isArray(rows)) || [];
    const source = students.length
      ? [...students].sort((a, b) => new Date(b.created_at || b.join_date || b.joined_date || 0) - new Date(a.created_at || a.join_date || a.joined_date || 0))
      : serverRows;
    return source.slice(0, 5);
  }, [dashboard, students]);

  const libraryNameFor = (student) => {
    const id = student.library_id ?? student.libraryId ?? student.libraryid;
    const library = id != null ? libraries.find(item => String(item.id ?? item.library_id) === String(id)) : null;
    return student.library_name || student.libraryName || library?.library_name || library?.name || 'Unknown';
  };

  const seatMix = useMemo(() => {
    const rows = Array.isArray(dashboard?.seatbreakdown) ? dashboard.seatbreakdown : [];
    const normalize = (category) => {
      const row = rows.find(item => String(item.category || '').toLowerCase().replace(/[^a-z]/g, '') === category);
      if (!row) return { total: 0, occupied: 0, available: 0, reserved: 0 };
      const total = Number(row.total || 0);
      const occupied = Number(row.occupied || 0);
      const available = Number(row.available || 0);
      return { total, occupied, available, reserved: Number(row.reserved ?? Math.max(0, total - occupied - available)) };
    };
    return [
      { key: 'ac', label: 'AC seats', accent: '#6366f1', ...normalize('ac') },
      { key: 'non-ac', label: 'Non-AC seats', accent: '#14b8a6', ...normalize('nonac') },
    ].map(item => ({ ...item, used: item.occupied + item.reserved, percent: item.total ? Math.round(((item.occupied + item.reserved) / item.total) * 100) : 0 }));
  }, [dashboard]);

  const paymentTotals = useMemo(() => {
    const now = new Date();
    const totals = { today: 0, month: 0, year: 0, total: 0 };
    payments.forEach(payment => {
      const rawDate = paymentDate(payment);
      const date = rawDate ? new Date(rawDate) : null;
      const amount = paymentAmount(payment);
      if (!date || Number.isNaN(date.getTime())) return;
      totals.total += amount;
      if (date.getFullYear() === now.getFullYear()) {
        totals.year += amount;
        if (date.getMonth() === now.getMonth()) {
          totals.month += amount;
          if (date.getDate() === now.getDate()) totals.today += amount;
        }
      }
    });
    return totals;
  }, [payments]);

  const planMix = useMemo(() => {
    const labels = { monthly: 'Monthly', quarterly: 'Quarterly', half_yearly: 'Half Yearly', yearly: 'Yearly' };
    const colors = { monthly: '#6366f1', quarterly: '#14b8a6', half_yearly: '#f59e0b', yearly: '#ec4899' };
    const raw = dashboard?.subscriptionBreakdown || dashboard?.subscription_breakdown;
    const counts = {};
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      Object.entries(raw).forEach(([key, value]) => { counts[key] = Number(value) || 0; });
    } else {
      students.forEach(student => {
        const key = student.subscription_type || student.subscriptiontype || student.subscription || 'monthly';
        counts[key] = (counts[key] || 0) + 1;
      });
    }
    const total = Object.values(counts).reduce((sum, value) => sum + value, 0);
    return Object.entries(counts)
      .filter(([, count]) => count > 0)
      .map(([key, count]) => ({
        key,
        label: labels[key] || key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase()),
        count,
        percent: total ? Math.round((count / total) * 100) : 0,
        color: colors[key] || '#94a3b8',
      }))
      .sort((a, b) => b.count - a.count);
  }, [dashboard, students]);

  const totalLibraries = statValue(dashboard, ['total_libraries', 'totallibraries'], libraries.length);
  const tenant = dashboard?.tenant || {};
  const todayLabel = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  // === PAYMENT MODAL LOGIC (FROM PAYMENTS PAGE) ===
  const handleStudentForPayment = async (student, type) => {
    const studentId = student.id || student.student_id || student.studentid;
    setSelectedStudent(student);
    setFormData(prev=>({...prev, studentid: String(studentId), paymentType: type==='renew' ? 'subscription' : type==='upgrade' ? 'upgrade' : 'subscription', renewalRequested: type==='renew'}));
    setShowAddModal(true);
    setStudentPlans([]); setOutstandingInfo(null); setSeatUpgrade(null);
    try{
      const [plansResult, outInfo, upgradeResult] = await Promise.all([
        getStudentPaymentPlans(studentId).catch(()=>[]),
        getStudentOutstanding(studentId).catch(()=>null),
        axios.get(`/api/students/${studentId}/seat-upgrade`).then(r=>r.data||null).catch(()=>null)
      ]);
      const plans=Array.isArray(plansResult)? plansResult : plansResult?.data||[];
      setStudentPlans(plans); setOutstandingInfo(outInfo?.data||outInfo);
      const activeUpgrade=(upgradeResult?.upgrade_id && upgradeResult?.status==='pending')? upgradeResult:null;
      setSeatUpgrade(activeUpgrade);
      if(outInfo?.plan_amount){ 
        setSelectedPlanAmount(outInfo.plan_amount); 
        const due=Number(outInfo.amount_due||0); 
        const def=due>0? String(due): String(outInfo.plan_amount||'');
        setFormData(prev=>({...prev, receivedAmount:def, amount:def})); 
      }
      if(plans.length>0 && !activeUpgrade){
        let selected=plans[0];
        if(outInfo?.plan_amount){ const amt=Number(outInfo.plan_amount); const match=plans.find(p=> Math.abs(Number(p.amount||0)-amt)<0.01); if(match) selected=match; }
        setFormData(prev=>({...prev, planid:String(selected.plan_id||selected.planid), amount:String(selected.amount||0), receivedAmount:String(outInfo?.amount_due||selected.amount||0)}));
        setSelectedPlanAmount(selected.amount||0);
      }
      if(activeUpgrade?.upgrade_id){
        const due=Number(activeUpgrade.amount_due||0);
        setFormData(prev=>({...prev, planid:'', receivedAmount:due>0?String(due):'', amount:due>0?String(due):'', paymentType: type==='upgrade' ? 'upgrade' : prev.paymentType, upgradeAmount:String(due)}));
        setSelectedPlanAmount(activeUpgrade.fee_amount||due);
      }
      if(type==='pay'){
        const due = outInfo?.amount_due || studentDue(student) || 0;
        if(due) setFormData(prev=>({...prev, receivedAmount:String(due), amount:String(due)}));
      }
    }catch{}
  };

  const handlePlanSelect = (e) => {
    const planId=e.target.value; const plan=studentPlans.find(p=> String(p.planid)===String(planId) || String(p.plan_id)===String(planId));
    setFormData(prev=>({...prev, planid:planId||'', amount:plan?String(plan.amount||0):'', receivedAmount:plan?String(plan.amount||0):prev.receivedAmount}));
    setSelectedPlanAmount(plan? (plan.amount||0):null);
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault(); setErrors({});
    if(!formData.studentid){ setErrors({student:'Please select a student'}); return; }
    if(!formData.receivedAmount || parseFloat(formData.receivedAmount)<=0){ setErrors({amount:'Enter valid amount'}); return; }
    if (savingPayment) return;
    setSavingPayment(true);

    // Disable event listeners during submission to prevent duplicate triggers
    const paymentSubmissionKey = `payment-submit-${Date.now()}`;
    try{
      if(seatUpgrade?.upgrade_id && formData.paymentType==='upgrade'){
        await axios.post(`/api/students/${formData.studentid}/seat-upgrade/${seatUpgrade.upgrade_id}/payment`,{ amount:parseFloat(formData.receivedAmount), payment_method:formData.paymentmethod, transaction_id:formData.transaction_id||null });
        toast?.('Seat Upgrade Paid');
      } else {
        await createPayment({ studentid:parseInt(formData.studentid), planid:formData.planid? parseInt(formData.planid):null, amount:parseFloat(formData.receivedAmount), received_amount:parseFloat(formData.receivedAmount), paymentmethod:formData.paymentmethod, transactionid:formData.transaction_id||null, send_reminder:Boolean(formData.sendReminder), renewal_requested:Boolean(formData.renewalRequested), pay_both:Boolean(formData.payBoth) });
        toast?.('Payment recorded');
      }
      // Close modal and reset form BEFORE dispatching event
      setShowAddModal(false);
      setFormData({studentid:'',planid:'',amount:'',receivedAmount:'',paymentmethod:'cash',transaction_id:'',status:'completed',sendReminder:false,renewalRequested:false,paymentType:'subscription',upgradeAmount:'',payBoth:false});
      setSelectedStudent(null); setStudentPlans([]); setSeatUpgrade(null);

      // Dispatch event AFTER modal is closed to prevent duplicate submissions
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('paymentDataUpdated', {
          detail: { studentId: formData.studentid, type: seatUpgrade ? 'seat-upgrade-payment' : 'subscription-payment', timestamp: paymentSubmissionKey }
        }));
      }, 100);

      // Refresh dashboard separately
      await loadDashboard(true, true);
    }catch(error){
      toast?.(error.error||'Failed');
      setSavingPayment(false);
    } finally{
      // Ensure savingPayment is set to false with a small delay
      setTimeout(() => setSavingPayment(false), 300);
    }
  };

  const handleDeleteStudent = async (student) => {
    const id = student.id ?? student.student_id ?? student.studentid;
    const name = student.name || student.full_name || 'this student';
    if (id == null || id === '') { toast?.('Student ID missing'); return; }
    if (!window.confirm(`Delete ${name}? This removes the student and frees their seat. Payment history is preserved.`)) return;
    setDeletingStudentId(String(id));
    try {
      await deleteStudent(id);
      window.dispatchEvent(new CustomEvent('studentDataUpdated', {
        detail: { studentId: id, action: 'deleted' }
      }));
      toast?.('Student deleted');
      await loadDashboard(true, true);
    } catch (error) {
      toast?.(error?.error || error?.message || 'Delete failed');
    } finally {
      setDeletingStudentId(null);
    }
  };

  const formatPlanOption=(plan)=>{ const name=plan.plan_name||plan.planname||'Plan'; const amt=plan.amount||0; const days=plan.duration_days||plan.durationdays||0; const cat=plan.seat_category||plan.seatcategory||''; return `${name} (${cat.toUpperCase()}) - ₹${amt} (${days} days)`; };

  const getUpiQrUrl = () => {
    try {
      const tenantStr = localStorage.getItem('tenantinfo');
      const tenant = tenantStr ? JSON.parse(tenantStr) : {};
      const qr = tenant.upi_qr_code || localStorage.getItem('payment_qr') || '';
      if (!qr) return '';
      return qr.startsWith('http') || qr.startsWith('data:') || qr.startsWith('blob:') ? qr : `${axios.defaults.baseURL || ''}${qr}`;
    } catch { return ''; }
  };

  const recentPayments = [...payments].sort((a, b) => new Date(paymentDate(b) || 0) - new Date(paymentDate(a) || 0));

  const totalStudents = statValue(dashboard, ['total_students', 'totalstudents'], students.length);
  const activeStudents = statValue(dashboard, ['active_students', 'activestudents'], students.filter(s => s.status !== 'inactive').length);
  const inactiveStudents = statValue(dashboard, ['inactive_students', 'inactivestudents'], Math.max(0, totalStudents - activeStudents));
  const totalRooms = statValue(dashboard, ['total_rooms', 'totalrooms']);
  const newThisMonth = statValue(dashboard, ['newstudentsmonth', 'new_students_month'], 0);
  const expiringToday = expiringSoon.filter(student => student._daysRemaining !== null && student._daysRemaining >= 0 && student._daysRemaining <= 1).length;
  const finance = {
    today: statValue(dashboard, ['todayrevenue', 'today_revenue', 'today_collection'], paymentTotals.today),
    month: statValue(dashboard, ['monthlyrevenue', 'monthly_revenue', 'monthrevenue'], paymentTotals.month),
    year: statValue(dashboard, ['yearlyrevenue', 'yearly_revenue', 'yearrevenue'], paymentTotals.year),
    total: statValue(dashboard, ['totalcollection', 'total_collection', 'totalrevenue'], paymentTotals.total),
  };
  const selectedLibraryLabel = allLibraries ? 'All libraries' : (activeLibrary?.library_name || selectedLibrary);

  const kpis = [
    { label: 'Total students', value: totalStudents, note: `${newThisMonth} new this month`, icon: <FiUsers />, tone: 'indigo', page: 'Students' },
    { label: 'Active students', value: activeStudents, note: `${inactiveStudents} inactive`, icon: <FiUserCheck />, tone: 'green', page: 'Students' },
    { label: 'Expiring soon', value: expiringSoon.length, note: expiringToday ? `${expiringToday} expiring today` : 'Upcoming renewals', icon: <FiClock />, tone: 'orange', page: 'Students' },
    { label: 'Occupancy rate', value: `${seatStats.percent}%`, note: `${seatStats.used} / ${seatStats.total} seats`, icon: <FiGrid />, tone: 'pink', page: 'Seats' },
    { label: 'Reserved seats', value: seatStats.reserved, note: 'Student personal seats', icon: <FiLayers />, tone: 'amber', page: 'Seats' },
    { label: 'Total libraries', value: totalLibraries, note: 'Managed libraries', icon: <FiBookOpen />, tone: 'blue' },
    { label: 'Total rooms', value: totalRooms, note: `across ${totalLibraries} libraries`, icon: <FiHome />, tone: 'violet' },
    { label: 'Unpaid students', value: outstandingStudents.length, note: `${outstandingStudents.length} outstanding dues`, icon: <FiAlertCircle />, tone: 'red', page: 'Students' },
  ];

  const outstandingPager = usePaged(outstandingStudents);
  const expiringPager = usePaged(expiringSoon);
  const recentPaymentsPager = usePaged(recentPayments);

  const dashboardHeader = (
    <header className="dash-header dash-page-header">
      <div className="dash-header-bar">
        <span className="dash-header-eyebrow">{toTitleCase(tenant?.name || tenant?.tenant_name || libraries?.[0]?.tenant_name || 'Library')} · {selectedLibraryLabel}</span>
        <p className="dash-header-today">
          <strong>{todayLabel}</strong>
          <span className="dash-header-dash">—</span>
          <span className="dash-header-greeting">{getTimeBasedGreeting()}</span>
        </p>
      </div>

      <div className="dash-header-main">
        <div className="dash-header-info">
          <h1 className="dash-header-title">Dashboard</h1>
          <p className="dash-header-subtitle">{getHeaderSubtitle()}</p>
        </div>
        <div className="dash-header-actions">
          <button type="button" className="dash-action-btn primary" onClick={openStudent}><FiPlus aria-hidden="true" /> Add student</button>
          <button type="button" className="dash-action-btn" onClick={() => setPage('Payments')}><FiCreditCard aria-hidden="true" /> Record payment</button>
          <button type="button" className="dash-action-btn" onClick={() => loadDashboard(true, true)} disabled={refreshing} aria-label="Refresh dashboard">
            <span className={`dash-action-icon ${refreshing ? 'is-loading' : ''}`}><FiRefreshCw aria-hidden="true" /></span>
            <span className="dash-action-label">{refreshing ? 'Refreshing' : 'Refresh'}</span>
          </button>
        </div>
      </div>
    </header>
  );

  if (loading) {
    return (
      <div className="dash-page">
        {dashboardHeader}
        <DashboardDataSkeleton />
      </div>
    );
  }

  return (
    <>
      <div className="dash-page">
      {dashboardHeader}

      <div className="dash-kpis">
        {kpis.map(item => {
          const clickable = Boolean(item.page);
          const Tag = clickable ? 'button' : 'article';
          return (
            <Tag
              className={`dash-kpi tone-${item.tone}${clickable ? ' is-clickable' : ''}`}
              key={item.label}
              {...(clickable
                ? {
                    type: 'button',
                    onClick: () => setPage(item.page),
                    'aria-label': `${item.label}: open ${item.page} page`,
                  }
                : {})}
            >
              <span className="dash-kpi-icon">{item.icon}</span>
              <span className="dash-kpi-copy">
                <span>{item.label}</span>
                <strong>{item.value}</strong>
                <small>{item.note}</small>
              </span>
            </Tag>
          );
        })}
      </div>

      <section className="panel dash-revenue-strip">
        <div className="dash-panel-head">
          <div><span className="dash-panel-kicker">Revenue</span><h2>Revenue overview</h2><p>Collections for the selected library</p></div>
          <button className="dash-link" onClick={() => setPage('Payments')}>View payments <FiArrowRight /></button>
        </div>
        <div className="dash-revenue-grid">
          <button type="button" className="dash-revenue-card tone-green" onClick={() => setPage('Payments')}>
            <span className="dash-revenue-icon"><FaRupeeSign /></span>
            <span className="dash-revenue-label">Today's collection</span>
            <strong><RupeeAmount value={finance.today} /></strong>
          </button>
          <button type="button" className="dash-revenue-card tone-blue" onClick={() => setPage('Payments')}>
            <span className="dash-revenue-icon"><FiCalendar /></span>
            <span className="dash-revenue-label">This month</span>
            <strong><RupeeAmount value={finance.month} /></strong>
          </button>
          {canViewFinancials && (
            <button type="button" className="dash-revenue-card tone-violet" onClick={() => setPage('Payments')}>
              <span className="dash-revenue-icon"><FiTrendingUp /></span>
              <span className="dash-revenue-label">This year</span>
              <strong><RupeeAmount value={finance.year} /></strong>
            </button>
          )}
          {canViewFinancials && (
            <button type="button" className="dash-revenue-card tone-teal" onClick={() => setPage('Payments')}>
              <span className="dash-revenue-icon"><FaRupeeSign /></span>
              <span className="dash-revenue-label">Total collection</span>
              <strong><RupeeAmount value={finance.total} /></strong>
            </button>
          )}
          {!canViewFinancials && <div className="dash-revenue-restricted">Yearly and lifetime financials are restricted for your role.</div>}
        </div>
      </section>

      <div className="dash-attention-grid">
        <section className="panel dash-attention-card">
          <div className="dash-panel-head">
            <div>
              <span className="dash-panel-kicker"><FiAlertCircle /> Action needed</span>
              <h2>Outstanding fees</h2>
              <p>{outstandingStudents.length} students · <RupeeAmount value={outstandingTotal} /> pending</p>
            </div>
            <button className="dash-link" onClick={()=>setPage('Payments')}>View payments <FiArrowRight /></button>
          </div>
          {outstandingStudents.length ? (
            <>
            <div className="dash-table-wrap">
              <table className="dash-table">
                <thead><tr><th>Student</th><th>Plan</th><th>Due</th><th></th></tr></thead>
                <tbody>
                  {outstandingPager.rows.map((student, index) => {
                    const name = student.name || student.full_name || 'Unnamed';
                    return <tr key={student.student_id || student.studentid || student.id || index}>
                      <td><b>{name}</b></td>
                      <td><span className="dash-plan-badge">{student.plan_name || student.planName || student.library_plan_status || 'No plan'}</span></td>
                      <td className="dash-due"><RupeeAmount value={student._amountDue} />{student.seat_upgrade_id ? <small>Seat upgrade</small> : null}</td>
                      <td><button className="dash-btn pay" onClick={()=>handleStudentForPayment(student, student.seat_upgrade_id ? 'upgrade' : 'pay')}>Pay</button></td>
                    </tr>;
                  })}
                </tbody>
              </table>
            </div>
            <DashPager page={outstandingPager.page} pages={outstandingPager.pages} setPage={outstandingPager.setPage} />
            </>
          ) : (
            <div className="dash-empty"><span><FiCheckCircle /></span><b>All fees collected</b><p>No outstanding balances right now.</p></div>
          )}
        </section>

        <section className="panel dash-attention-card">
          <div className="dash-panel-head">
            <div>
              <span className="dash-panel-kicker"><FiCalendar /> Renewals</span>
              <h2>Subscriptions expiring</h2>
              <p>{expiringSoon.length} subscriptions need attention</p>
            </div>
            <button className="dash-link" onClick={()=>setPage('Students')}>View students <FiArrowRight /></button>
          </div>
          {expiringSoon.length ? (
            <>
            <div className="dash-table-wrap">
              <table className="dash-table">
                <thead><tr><th>Student</th><th>Expires</th><th>Time left</th><th></th></tr></thead>
                <tbody>
                  {expiringPager.rows.map((student, index) => {
                    const days = student._daysRemaining;
                    const name = student.name || student.full_name || 'Unnamed';
                    const dayLabel = days == null ? 'â€”' : days < 0 ? `${Math.abs(days)} day${Math.abs(days)===1?'':'s'} ago` : days === 0 ? 'Today' : `${days} day${days===1?'':'s'}`;
                    return <tr key={student.student_id || student.studentid || student.id || index}>
                      <td><b>{name}</b></td>
                      <td>{subscriptionEnd(student) ? new Date(subscriptionEnd(student)).toLocaleDateString('en-IN') : 'â€”'}</td>
                      <td><span className={`days-pill ${days == null || days <= 1 ? 'danger' : days <= 3 ? 'warn' : 'info'}`}>{dayLabel}</span></td>
                      <td>
                        <div className="dash-row-actions">
                          <button className="dash-btn renew" onClick={()=>handleStudentForPayment(student,'renew')}>Renew</button>
                          {days !== null && days < 0 && (
                            <button className="dash-btn delete" disabled={deletingStudentId === studentKey(student)} onClick={()=>handleDeleteStudent(student)}>
                              {deletingStudentId === studentKey(student) ? 'Deleting...' : 'Delete'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>;
                  })}
                </tbody>
              </table>
            </div>
            <DashPager page={expiringPager.page} pages={expiringPager.pages} setPage={expiringPager.setPage} />
            </>
          ) : (
            <div className="dash-empty"><span><FiCheckCircle /></span><b>Nothing expiring soon</b><p>No expired or expiring subscriptions found.</p></div>
          )}
        </section>
      </div>

      <div className="dash-charts-grid">
        <FigmaRevenueChart months={revenue.months} values={revenue.monthly} max={revenue.max} />
        <FigmaOccupancy total={seatStats.total} occupied={seatStats.occupied} available={seatStats.available} reserved={seatStats.reserved} percent={seatStats.percent} mix={seatMix} setPage={setPage} />
      </div>

      <div className="dash-lower-grid">
        <FigmaGrowthChart months={growth.months} counts={growth.counts} max={growth.max} />
        <section className="panel dash-plan-card">
          <div className="dash-panel-head">
            <div><span className="dash-panel-kicker">Subscriptions</span><h2>Plan mix</h2><p>Across active students</p></div>
          </div>
          {planMix.length ? (
            <div className="dash-plan-list">
              {planMix.map(plan => (
                <div className="dash-plan-row" key={plan.key}>
                  <div className="dash-plan-top"><span><i style={{ background: plan.color }} />{plan.label}</span><b>{plan.count}</b></div>
                  <div className="dash-plan-bar"><i style={{ width: `${plan.percent}%`, background: plan.color }} /></div>
                  <small>{plan.percent}% of subscriptions</small>
                </div>
              ))}
            </div>
          ) : <div className="dash-empty compact"><span><FiLayers /></span><b>No plan data</b><p>Plans will appear here once students subscribe.</p></div>}
        </section>
        <section className="panel dash-quick-card">
          <div className="dash-panel-head"><div><span className="dash-panel-kicker">Shortcuts</span><h2>Quick actions</h2><p>Common tasks</p></div></div>
          <div className="dash-quick-list">
            <button className="dash-quick-item" onClick={openStudent}><span className="dash-quick-icon blue"><FiUserPlus /></span><span className="dash-quick-copy"><b>Add student</b><small>Enroll a new member</small></span><FiArrowRight /></button>
            <button className="dash-quick-item" onClick={()=>setPage('Payments')}><span className="dash-quick-icon green"><FiCreditCard /></span><span className="dash-quick-copy"><b>Record payment</b><small>Collect a fee</small></span><FiArrowRight /></button>
            <button className="dash-quick-item" onClick={()=>setPage('Seats')}><span className="dash-quick-icon violet"><FiGrid /></span><span className="dash-quick-copy"><b>Seat management</b><small>View seat allocations</small></span><FiArrowRight /></button>
            <button className="dash-quick-item" onClick={()=>setPage('Libraries')}><span className="dash-quick-icon amber"><FiBookOpen /></span><span className="dash-quick-copy"><b>Library management</b><small>Manage libraries and rooms</small></span><FiArrowRight /></button>
          </div>
        </section>
      </div>

      </div>

      {/* PAYMENT MODAL - SAME AS PAYMENTS PAGE */}
      {showAddModal && (
        <div className="modal-backdrop" onMouseDown={e=> e.target===e.currentTarget && setShowAddModal(false)}>
          <section className="payment-modal">
            <div className="payment-modal-header">
              <div>
                <h2>{formData.renewalRequested ? <><FiRefreshCw /> Renew Subscription</> : formData.paymentType==='upgrade' ? <><FiCreditCard /> Pay Seat Upgrade</> : <><FiDollarSign /> Pay Remaining</>}</h2>
                <small>{selectedStudent ? `${selectedStudent.name} · ${selectedStudent.phone||''}` : ''}</small>
              </div>
              <button onClick={()=>setShowAddModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmitPayment} className="payment-form">
              <div className="form-grid-2">
                <label>Student *<div className="student-select-row"><input readOnly value={selectedStudent? selectedStudent.name:''} /><span style={{fontSize:'11px',color:'#64748b'}}>Locked from Dashboard</span></div></label>
                <label>Plan<UiSelect value={formData.planid} onChange={handlePlanSelect}><option value="">{seatUpgrade? 'Upgrade payment':'Select plan'}</option>{studentPlans.map(p=> <option key={p.plan_id||p.planid} value={p.plan_id||p.planid}>{formatPlanOption(p)}</option>)}</UiSelect></label>
              </div>
              <div className="form-grid-2">
                <label>Plan Amount<input value={selectedPlanAmount? `₹${selectedPlanAmount}`:'Auto-fill'} readOnly className="readonly" />{outstandingInfo && <span className="due-text">Due: ₹{outstandingInfo.amount_due||0} | Plan: ₹{outstandingInfo.plan_amount||selectedPlanAmount||0}</span>}</label>
                <label>Amount Received *<input type="number" step="0.01" value={formData.receivedAmount} onChange={e=> setFormData({...formData, receivedAmount:e.target.value})} />{errors.amount && <span className="error">{errors.amount}</span>}</label>
              </div>
              {seatUpgrade && (<div className="upgrade-box"><b>Reserved Seat Upgrade:</b> Fee ₹{seatUpgrade.fee_amount||seatUpgrade.amount_due} - Due ₹{seatUpgrade.amount_due}<div className="radio-group"><label><input type="radio" checked={formData.paymentType==='upgrade'} onChange={()=>setFormData({...formData, paymentType:'upgrade'})} /> Pay Upgrade</label><label><input type="radio" checked={formData.paymentType==='subscription'} onChange={()=>setFormData({...formData, paymentType:'subscription'})} /> Pay Subscription</label></div></div>)}
              <div className="form-grid-2">
                <label className="checkbox-label"><input type="checkbox" checked={formData.sendReminder} onChange={e=>setFormData({...formData, sendReminder:e.target.checked})} /> Send reminder</label>
                <label>Payment Method *<UiSelect value={formData.paymentmethod} onChange={e=>setFormData({...formData, paymentmethod:e.target.value})}><option value="cash">Cash</option><option value="upi">UPI</option></UiSelect></label>
              </div>

              {formData.paymentmethod === 'upi' && (
                <div className="upi-qr-box">
                  <h4 className="upi-qr-title">Scan to pay with UPI</h4>
                  {getUpiQrUrl() ? (
                    <>
                      <img src={getUpiQrUrl()} alt="UPI QR" className="upi-qr-img" onError={e=>e.target.style.display='none'} />
                      <p className="upi-qr-amount">Show this QR to the student. Amount: <b>₹{Number(formData.receivedAmount || 0).toLocaleString('en-IN')}</b></p>
                      <button type="button" className="upi-view-btn" onClick={()=>window.open(getUpiQrUrl(), '_blank', 'noopener,noreferrer')}>View full QR</button>
                    </>
                  ) : (
                    <div className="upi-no-qr">No UPI QR uploaded. Go to Settings &gt; QR Management to upload one.</div>
                  )}
                </div>
              )}

              <label>Transaction ID<input value={formData.transaction_id} onChange={e=>setFormData({...formData, transaction_id:e.target.value})} placeholder="Optional for cash" /></label>
              <div className="modal-actions"><button type="button" className="cancel-btn" onClick={()=>setShowAddModal(false)}>Cancel</button><button type="submit" className="save-btn" disabled={savingPayment}>{savingPayment ? 'Saving...' : formData.renewalRequested ? 'Renew Now' : 'Pay Now'}</button></div>
            </form>
          </section>
        </div>
      )}

      <div className="dash-page">
      <div className="dash-recent-grid">
        <section className="panel dash-recent-card">
          <div className="dash-panel-head">
            <div><span className="dash-panel-kicker">Latest joins</span><h2>Recent students</h2><p>{students.length} total students</p></div>
            <button className="dash-link" onClick={()=>setPage('Students')}>View all <FiArrowRight /></button>
          </div>
          {recentStudentsLive.length ? (
            <>
            <div className="dash-table-wrap">
              <table className="dash-table dash-recent-students-table">
                <thead><tr><th>Name</th><th>Library</th><th>Status</th></tr></thead>
                <tbody>
                  {recentStudentsLive.map((student, index) => {
                    const name = student.name || student.full_name || 'Unnamed';
                    const status = String(student.status || 'active').toLowerCase();
                    return <tr key={studentKey(student) || index}>
                      <td><b>{name}</b></td>
                      <td>{libraryNameFor(student)}</td>
                      <td><span className={`dash-status ${status}`}>{student.status || 'active'}</span></td>
                    </tr>;
                  })}
                </tbody>
              </table>
            </div>
            </>
          ) : <div className="dash-empty compact"><span><FiUsers /></span><b>No students yet</b><p>Recently enrolled students will show up here.</p></div>}
        </section>

        <section className="panel dash-recent-card">
          <div className="dash-panel-head">
            <div><span className="dash-panel-kicker">Money in</span><h2>Recent payments</h2><p>5 per page</p></div>
            <button className="dash-link" onClick={()=>setPage('Payments')}>View all <FiArrowRight /></button>
          </div>
          {recentPayments.length ? (
            <>
            <div className="dash-table-wrap">
              <table className="dash-table">
                <thead><tr><th>Student</th><th>Amount</th><th>Date</th><th>Status</th></tr></thead>
                <tbody>
                  {recentPaymentsPager.rows.map(payment => {
                    const status = String(payment.status || payment.payment_status || 'completed').toLowerCase();
                    const date = paymentDate(payment);
                    return <tr key={payment.id || payment.payment_id}>
                      <td><b>{payment.student_name || payment.studentname || payment.name || 'Unknown'}</b></td>
                      <td className="dash-collected"><RupeeAmount value={paymentAmount(payment)} /></td>
                      <td>{date ? new Date(date).toLocaleDateString('en-IN') : 'â€”'}</td>
                      <td><span className={`dash-status ${status}`}>{payment.status || payment.payment_status || 'Completed'}</span></td>
                    </tr>;
                  })}
                </tbody>
              </table>
            </div>
            <DashPager page={recentPaymentsPager.page} pages={recentPaymentsPager.pages} setPage={recentPaymentsPager.setPage} />
            </>
          ) : <div className="dash-empty compact"><span><FiCreditCard /></span><b>No payments yet</b><p>Recorded payments will appear here.</p></div>}
        </section>
      </div>
      </div>
    </>
  );
}

function FigmaRevenueChart({ months, values, max }){
  const [tip, setTip] = useState(null);
  
  // FIX 1: Build full 7-month list even if API gives only Oct
  const fullData = useMemo(() => {
    const map = {};
    months.forEach((m,i)=>{ map[m.slice(0,3)] = values[i]||0; });
    // If only 1 month data, show last 7 months with 0 for missing
    if(months.length <= 1){
      const last7 = ['Apr','May','Jun','Jul','Aug','Sep','Oct'];
      return { months: last7, values: last7.map(m=> map[m]||0) };
    }
    return { months, values };
  }, [months, values]);

  const displayMonths = fullData.months;
  const displayValues = fullData.values;
  if(!displayMonths.length) return <section className="panel reference-chart"><div className="dash-panel-head"><div><span className="dash-panel-kicker">Collections</span><h2>Revenue overview</h2></div></div><div className="dash-empty compact"><span><FiDollarSign /></span><b>No revenue data</b><p>Payments will appear here.</p></div></section>;

  const dataMax = Math.max(...displayValues, 1);
  const chartMax = Math.ceil(dataMax * 1.4); // auto-scale so dot is visible
  const finalMax = Math.max(chartMax, 25000);

  const W=600, H=260, P={t:20,r:20,b:50,l:65};
  const chartW = W-P.l-P.r, chartH = H-P.t-P.b;

  const pts = displayValues.map((v,i)=>{
    const x = displayMonths.length===1 ? P.l+chartW/2 : P.l + (i/(displayMonths.length-1))*chartW;
    const y = P.t + (1 - v/(finalMax||1))*chartH;
    return { x, y, v, m: displayMonths[i] };
  });

  let d = `M ${pts[0].x} ${pts[0].y}`;
  for(let i=1;i<pts.length;i++){ const prev=pts[i-1], cur=pts[i]; const cx=(prev.x+cur.x)/2; d+=` C ${cx} ${prev.y}, ${cx} ${cur.y}, ${cur.x} ${cur.y}`; }
  const area = `${d} L ${pts[pts.length-1].x} ${P.t+chartH} L ${pts[0].x} ${P.t+chartH} Z`;

  return(
    <section className="panel reference-chart">
      <div className="dash-panel-head"><div><span className="dash-panel-kicker">Collections</span><h2>Revenue overview</h2><p>Last {displayMonths.length} months</p></div><span className="dash-total-chip"><RupeeAmount value={displayValues.reduce((sum, value) => sum + value, 0)} /></span></div>
      <div style={{height:260, position:'relative'}} onMouseLeave={()=>setTip(null)}>
        <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%', height:'100%', overflow:'visible'}}>
          <defs><linearGradient id="revFillNew" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#6366f1" stopOpacity="0.25"/><stop offset="100%" stopColor="#6366f1" stopOpacity="0"/></linearGradient></defs>
          
          {/* Y AXIS - PROPERLY ALIGNED LEFT */}
          {[0,1,2,3,4].map(i=>{
            const y = P.t + (i/4)*chartH;
            const val = Math.round(finalMax*(1-i/4));
            return <g key={i}>
              <line x1={P.l} x2={W-P.r} y1={y} y2={y} stroke="#eef2f7" strokeWidth="1"/>
              <text x={P.l-12} y={y+4} fontSize="11" fill="#64748b" textAnchor="end" fontWeight="600">₹{val>=1000? `${Math.round(val/1000)}k` : val}</text>
            </g>
          })}
          {/* X+Y AXIS LINES - ALIGNED */}
          <line x1={P.l} x2={W-P.r} y1={P.t+chartH} y2={P.t+chartH} stroke="#334155" strokeWidth="1.5"/>
          <line x1={P.l} x2={P.l} y1={P.t} y2={P.t+chartH} stroke="#334155" strokeWidth="1.5"/>

          <path d={area} fill="url(#revFillNew)"/>
          <path d={d} fill="none" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round"/>

          {pts.map((p,i)=><g key={i}>
            <circle cx={p.x} cy={p.y} r={tip?.i===i?6:4.5} fill="#6366f1" stroke="#fff" strokeWidth="2" style={{cursor:'pointer'}} onMouseEnter={()=>setTip({i})}/>
            {/* X LABELS ALIGNED UNDER EACH POINT */}
            <text x={p.x} y={P.t+chartH+22} fontSize="11" fill="#475569" textAnchor="middle" fontWeight="600">{p.m}</text>
          </g>)}
        </svg>
        {tip!=null && <div style={{position:'absolute', left:`${(pts[tip.i].x/W)*100}%`, top:`${pts[tip.i].y-10}px`, transform:'translate(-50%,-110%)', background:'#0f172a', color:'#fff', padding:'6px 10px', borderRadius:'8px', fontSize:'11px', fontWeight:'700', whiteSpace:'nowrap'}}>₹{pts[tip.i].v.toLocaleString('en-IN')}</div>}
      </div>
    </section>
  )
}function FigmaOccupancy({ total, occupied, available, reserved, percent, mix = [], setPage }) {
  const legend = [
    { label: 'Occupied', value: occupied, color: '#6366f1' },
    { label: 'Available', value: available, color: '#14b8a6' },
    { label: 'Reserved', value: reserved, color: '#f59e0b' },
  ];
  return (
    <section className="panel dash-occupancy-card">
      <div className="dash-panel-head">
        <div><span className="dash-panel-kicker">Capacity</span><h2>Seat occupancy</h2><p>{total} seats across all rooms</p></div>
        <button className="dash-link" onClick={()=>setPage('Seats')}>Details <FiArrowRight /></button>
      </div>
      <div className="dash-occ-main">
        <div className="dash-donut">
          <svg viewBox="0 0 42 42" aria-hidden="true">
            <circle r="15.915" cx="21" cy="21" fill="transparent" stroke="var(--dash-track)" strokeWidth="5.5" />
            <circle r="15.915" cx="21" cy="21" fill="transparent" stroke="#6366f1" strokeWidth="5.5" strokeDasharray={`${percent} ${100 - percent}`} strokeLinecap="round" />
          </svg>
          <div className="dash-donut-label"><b>{percent}%</b><span>Occupied</span></div>
        </div>
        <div className="dash-occ-legend">
          {legend.filter(item => item.value > 0 || item.label !== 'Reserved').map(item => (
            <div className="dash-occ-legend-row" key={item.label}>
              <span><i style={{ background: item.color }} />{item.label}</span>
              <b>{item.value}</b>
              <small>{total ? Math.round((item.value / total) * 100) : 0}%</small>
            </div>
          ))}
        </div>
      </div>
      <div className="dash-occ-split">
        {mix.map(item => (
          <div className="dash-occ-split-item" key={item.key}>
            <div className="dash-occ-split-head"><span>{item.label}</span><b>{item.occupied}/{item.total}</b></div>
            <div className="dash-progress"><i style={{ width: `${item.percent}%`, background: item.accent }} /></div>
            <small>{item.available} available</small>
          </div>
        ))}
      </div>
    </section>
  );
}
function FigmaGrowthChart({ months, counts, max }){
  const [tip,setTip]=useState(null);
  
  // FIX: Build full months if API gives only few
  const fullData = useMemo(() => {
    if(!months.length) return { months:[], counts:[] };
    if(months.length <= 1){
      const base = ['Apr','May','Jun','Jul','Aug','Sep','Oct'];
      const map = {}; months.forEach((m,i)=> map[m.slice(0,3)] = counts[i]||0);
      return { months: base, counts: base.map(m=> map[m]||0) };
    }
    return { months, counts };
  }, [months, counts]);

  const displayMonths = fullData.months;
  const displayCounts = fullData.counts;
  
  if(!displayMonths.length) return <section className="panel growth-panel"><div className="dash-panel-head"><div><span className="dash-panel-kicker">Growth</span><h2>Student growth</h2></div></div><div className="dash-empty compact"><span><FiUsers /></span><b>No growth data</b><p>New students will appear here.</p></div></section>;

  const dataMax = Math.max(...displayCounts, 1);
  const chartMax = Math.ceil(dataMax * 1.3); // headroom

  const W=600, H=260, P={t:20,r:20,b:50,l:50};
  const chartW = W-P.l-P.r, chartH = H-P.t-P.b;
  const barSlot = chartW / displayMonths.length;
  const barW = Math.min(36, barSlot * 0.5);

  return(
    <section className="panel growth-panel" style={{position:'relative'}}>
      <div className="dash-panel-head"><div><span className="dash-panel-kicker">Growth</span><h2>Student growth</h2><p>Last {displayMonths.length} months</p></div><span className="dash-total-chip">{displayCounts.reduce((sum, value) => sum + value, 0)} total</span></div>
      <div style={{height:260, position:'relative'}} onMouseLeave={()=>setTip(null)}>
        <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%', height:'100%', overflow:'visible'}}>
          {/* Y AXIS */}
          {[0,1,2,3,4].map(i=>{
            const y = P.t + (i/4)*chartH;
            const val = Math.round(chartMax*(1-i/4));
            return <g key={i}>
              <line x1={P.l} x2={W-P.r} y1={y} y2={y} stroke="#eef2f7" strokeWidth="1"/>
              <text x={P.l-12} y={y+4} fontSize="11" fill="#64748b" textAnchor="end" fontWeight="600">{val}</text>
            </g>
          })}
          {/* X + Y AXIS LINES */}
          <line x1={P.l} x2={W-P.r} y1={P.t+chartH} y2={P.t+chartH} stroke="#334155" strokeWidth="1.5"/>
          <line x1={P.l} x2={P.l} y1={P.t} y2={P.t+chartH} stroke="#334155" strokeWidth="1.5"/>

          {displayCounts.map((v,i)=>{
            const h = (v/(chartMax||1))*chartH;
            const x = P.l + (i+0.5)*barSlot - barW/2;
            const y = P.t+chartH - h;
            return <g key={displayMonths[i]}>
              <rect x={x} y={y} width={barW} height={h} rx="6" 
                fill={tip?.i===i ? '#4f46e5' : '#6366f1'} 
                style={{cursor:'pointer', transition:'all .2s'}}
                onMouseEnter={()=>setTip({i})}/>
              {/* X LABEL ALIGNED UNDER BAR */}
              <text x={x+barW/2} y={P.t+chartH+22} fontSize="11" fill="#475569" textAnchor="middle" fontWeight="600">{displayMonths[i]}</text>
            </g>
          })}
        </svg>
        {tip!=null && <div style={{position:'absolute', left:`${(P.l + (tip.i+0.5)*barSlot)/W*100}%`, top:'12px', transform:'translateX(-50%)', background:'#0f172a', color:'#fff', padding:'6px 10px', borderRadius:'8px', fontSize:'11px', fontWeight:'700'}}>{displayMonths[tip.i]}: {displayCounts[tip.i]} students</div>}
      </div>
    </section>
  )
}
